export const SANDBOX_CRON_TIMEZONE = '[UTC+8] Singapore'
export const DEFAULT_SANDBOX_CRON_SYNTAX = '0 22 * * 5'
export const DEFAULT_SANDBOX_CRON_DESCRIPTION =
	'Runs every Friday at 10:00 PM SGT'

export const SANDBOX_SCHEDULE_MODE = {
	DEFAULT: 'default',
	CUSTOM: 'custom',
	ADVANCED: 'advanced'
} as const

export type SandboxScheduleMode =
	(typeof SANDBOX_SCHEDULE_MODE)[keyof typeof SANDBOX_SCHEDULE_MODE]

export const SANDBOX_WEEKDAY_OPTIONS = [
	{ label: 'Sunday', value: '0' },
	{ label: 'Monday', value: '1' },
	{ label: 'Tuesday', value: '2' },
	{ label: 'Wednesday', value: '3' },
	{ label: 'Thursday', value: '4' },
	{ label: 'Friday', value: '5' },
	{ label: 'Saturday', value: '6' }
]

export const SANDBOX_FREQUENCY_OPTIONS = [
	{ label: 'Daily', value: 'daily' },
	{ label: 'Weekly', value: 'weekly' },
	{ label: 'Monthly', value: 'monthly' },
	{ label: 'Yearly', value: 'yearly' }
]

export const SANDBOX_DAY_OF_MONTH_OPTIONS = Array.from(
	{ length: 31 },
	(_, index) => ({ label: String(index + 1), value: String(index + 1) })
)

export const SANDBOX_MONTH_OPTIONS = [
	{ label: 'January', value: '1' },
	{ label: 'February', value: '2' },
	{ label: 'March', value: '3' },
	{ label: 'April', value: '4' },
	{ label: 'May', value: '5' },
	{ label: 'June', value: '6' },
	{ label: 'July', value: '7' },
	{ label: 'August', value: '8' },
	{ label: 'September', value: '9' },
	{ label: 'October', value: '10' },
	{ label: 'November', value: '11' },
	{ label: 'December', value: '12' }
]

export const SANDBOX_TIME_OPTIONS = Array.from({ length: 24 }, (_, hour) => {
	const displayHour = hour % 12 || 12
	const suffix = hour < 12 ? 'AM' : 'PM'

	return {
		label: `${displayHour}:00 ${suffix}`,
		value: `${String(hour).padStart(2, '0')}:00`
	}
})

export const DEFAULT_SANDBOX_SCHEDULE_DAY = '5'
export const DEFAULT_SANDBOX_SCHEDULE_TIME = '22:00'
export const DEFAULT_SANDBOX_SCHEDULE_FREQUENCY = 'weekly'
export const DEFAULT_SANDBOX_SCHEDULE_DAY_OF_MONTH = '1'
export const DEFAULT_SANDBOX_SCHEDULE_MONTH = '1'

const getOptionLabel = (
	options: Array<{ label: string; value: string }>,
	value: unknown,
	fallbackValue: string
): string =>
	options.find((option) => option.value === String(value))?.label ??
	options.find((option) => option.value === fallbackValue)?.label ??
	''

export const getSandboxScheduleMode = (value: unknown): SandboxScheduleMode =>
	value === SANDBOX_SCHEDULE_MODE.CUSTOM || value === SANDBOX_SCHEDULE_MODE.ADVANCED
		? value
		: SANDBOX_SCHEDULE_MODE.DEFAULT

const getValidOptionValue = (
	options: Array<{ label: string; value: string }>,
	value: unknown,
	fallbackValue: string
): string =>
	options.some((option) => option.value === String(value))
		? String(value)
		: fallbackValue

const getOrdinal = (value: string): string => {
	const number = Number(value)
	const suffix =
		number % 10 === 1 && number % 100 !== 11
			? 'st'
			: number % 10 === 2 && number % 100 !== 12
				? 'nd'
				: number % 10 === 3 && number % 100 !== 13
					? 'rd'
					: 'th'

	return `${number}${suffix}`
}

export const getSandboxScheduleFrequency = (value: unknown): string =>
	getValidOptionValue(
		SANDBOX_FREQUENCY_OPTIONS,
		value,
		DEFAULT_SANDBOX_SCHEDULE_FREQUENCY
	)

export const buildSandboxCronSyntax = ({
	frequency,
	day,
	dayOfMonth,
	month,
	time
}: {
	frequency: unknown
	day: unknown
	dayOfMonth: unknown
	month: unknown
	time: unknown
}): string => {
	const resolvedDay = SANDBOX_WEEKDAY_OPTIONS.some(
		(option) => option.value === String(day)
	)
		? String(day)
		: DEFAULT_SANDBOX_SCHEDULE_DAY
	const resolvedTime = SANDBOX_TIME_OPTIONS.some(
		(option) => option.value === String(time)
	)
		? String(time)
		: DEFAULT_SANDBOX_SCHEDULE_TIME
	const [hour] = resolvedTime.split(':')
	const resolvedFrequency = getSandboxScheduleFrequency(frequency)
	const resolvedDayOfMonth = getValidOptionValue(
		SANDBOX_DAY_OF_MONTH_OPTIONS,
		dayOfMonth,
		DEFAULT_SANDBOX_SCHEDULE_DAY_OF_MONTH
	)
	const resolvedMonth = getValidOptionValue(
		SANDBOX_MONTH_OPTIONS,
		month,
		DEFAULT_SANDBOX_SCHEDULE_MONTH
	)

	switch (resolvedFrequency) {
		case 'daily':
			return `0 ${Number(hour)} * * *`
		case 'monthly':
			return `0 ${Number(hour)} ${resolvedDayOfMonth} * *`
		case 'yearly':
			return `0 ${Number(hour)} ${resolvedDayOfMonth} ${resolvedMonth} *`
		default:
			return `0 ${Number(hour)} * * ${resolvedDay}`
	}
}

export const resolveSandboxScheduleCronSyntax = ({
	mode,
	frequency,
	day,
	dayOfMonth,
	month,
	time,
	cronSyntax
}: {
	mode: unknown
	frequency: unknown
	day: unknown
	dayOfMonth: unknown
	month: unknown
	time: unknown
	cronSyntax: unknown
}): string => {
	if (getSandboxScheduleMode(mode) === SANDBOX_SCHEDULE_MODE.CUSTOM) {
		return buildSandboxCronSyntax({ frequency, day, dayOfMonth, month, time })
	}

	if (getSandboxScheduleMode(mode) === SANDBOX_SCHEDULE_MODE.ADVANCED) {
		return resolveSandboxCronSyntax(cronSyntax)
	}

	return DEFAULT_SANDBOX_CRON_SYNTAX
}

export const describeSandboxSchedule = ({
	mode,
	frequency,
	day,
	dayOfMonth,
	month,
	time,
	cronSyntax
}: {
	mode: unknown
	frequency: unknown
	day: unknown
	dayOfMonth: unknown
	month: unknown
	time: unknown
	cronSyntax: unknown
}): string => {
	const scheduleMode = getSandboxScheduleMode(mode)

	if (scheduleMode === SANDBOX_SCHEDULE_MODE.DEFAULT) {
		return DEFAULT_SANDBOX_CRON_DESCRIPTION
	}

	if (scheduleMode === SANDBOX_SCHEDULE_MODE.ADVANCED) {
		return `Custom cron: ${resolveSandboxCronSyntax(cronSyntax)}`
	}

	const weekday = getOptionLabel(
		SANDBOX_WEEKDAY_OPTIONS,
		day,
		DEFAULT_SANDBOX_SCHEDULE_DAY
	)
	const scheduledTime = getOptionLabel(
		SANDBOX_TIME_OPTIONS,
		time,
		DEFAULT_SANDBOX_SCHEDULE_TIME
	)
	const resolvedFrequency = getSandboxScheduleFrequency(frequency)
	const dayOfMonthLabel = getOptionLabel(
		SANDBOX_DAY_OF_MONTH_OPTIONS,
		dayOfMonth,
		DEFAULT_SANDBOX_SCHEDULE_DAY_OF_MONTH
	)

	if (resolvedFrequency === 'daily') {
		return `Runs daily at ${scheduledTime} SGT`
	}

	if (resolvedFrequency === 'monthly') {
		return `Runs monthly on the ${getOrdinal(dayOfMonthLabel)} at ${scheduledTime} SGT`
	}

	if (resolvedFrequency === 'yearly') {
		const monthLabel = getOptionLabel(
			SANDBOX_MONTH_OPTIONS,
			month,
			DEFAULT_SANDBOX_SCHEDULE_MONTH
		)
		return `Runs yearly on ${monthLabel} ${dayOfMonthLabel} at ${scheduledTime} SGT`
	}

	return `Runs weekly on ${weekday} at ${scheduledTime} SGT`
}

export const resolveSandboxCronSyntax = (value: unknown): string => {
	if (typeof value === 'string') {
		const trimmedValue = value.trim()

		return trimmedValue || DEFAULT_SANDBOX_CRON_SYNTAX
	}

	if (value === undefined || value === null) {
		return DEFAULT_SANDBOX_CRON_SYNTAX
	}

	const trimmedValue = String(value).trim()

	return trimmedValue || DEFAULT_SANDBOX_CRON_SYNTAX
}

export const getSandboxCronSyntaxError = (value: unknown): string | undefined => {
	const cronSyntax = typeof value === 'string' ? value.trim() : ''

	if (!cronSyntax) {
		return 'Cron expression is required when using Advanced mode.'
	}

	const fields = cronSyntax.split(/\s+/)
	if (fields.length !== 5) {
		return 'Cron expression must contain five fields: minute, hour, day of month, month, and day of week.'
	}

	if (fields.some((field) => !/^[0-9A-Za-z*?,/#%-]+$/.test(field))) {
		return 'Cron expression contains unsupported characters.'
	}

	return undefined
}
